import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  FormControlLabel,
  Grid,
  IconButton,
  MenuItem,
  Snackbar,
  Stack,
  Switch,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  AddPhotoAlternate as AddPhotoAlternateIcon,
  ArrowBack as ArrowBackIcon,
  ArrowDownward as ArrowDownwardIcon,
  ArrowUpward as ArrowUpwardIcon,
  Close as CloseIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  HighlightAlt as HighlightAltIcon,
  Save as SaveIcon,
  SwapHoriz as SwapHorizIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
} from '@mui/icons-material';
import {
  api,
  DistrictOption,
  MasterToolSearchFilters,
  ProjectCategoryOption,
  SuperShowcase,
  SuperShowcaseActionConfig,
  SuperShowcaseActionType,
  SuperShowcaseHotspotInput,
  SuperShowcaseHotspotShape,
  SuperShowcaseLinkConfig,
  SuperShowcaseProjectResearchConfig,
  SuperShowcasePathPoint,
  SuperShowcasePathValue,
} from '../../services/api';
import {
  MAX_LINK_URL_LENGTH,
  SHOWCASE_ACCENT_PALETTE,
  SHOWCASE_ACTIONS,
  describeHotspotAction,
  getHotspotColor,
  getShowcaseActionMeta,
  normalizeShowcaseLinkUrl,
} from '../../components/showcase/showcaseActions';
import FreehandPolygonOverlay, { normalizePathRings } from '../../components/showcase/FreehandPolygonOverlay';
import { ShowcaseViewStatsLine, useShowcaseViewStats } from '../../components/showcase/ShowcaseViewStats';

/**
 * Admin dialog: "Manage Super Showcase".
 *
 * Flow — upload an image, drag on the preview to mark out clickable areas,
 * then map each area to a Super Picker function (Price Promo, Thumbsup, a
 * Project Research search whose criteria are pre-filled here, or a hyperlink
 * that opens in a new tab). Agents see the published showcases under
 * Tools → Super Showcase.
 *
 * Backed by `/api/super-showcase/admin/showcases` (see routes/superPickerShowcase.ts).
 */

const MAX_IMAGE_BYTES = 6 * 1024 * 1024;
/** Anything smaller than this (in % of the image) is treated as a stray click. */
const MIN_HOTSPOT_PERCENT = 1.5;

interface DraftHotspot {
  /** Stable client-side key; hotspots are re-created server-side on every save. */
  key: string;
  label: string;
  shape: SuperShowcaseHotspotShape;
  x: number | null;
  y: number | null;
  width: number | null;
  height: number | null;
  pathData: SuperShowcasePathValue | null;
  actionType: SuperShowcaseActionType;
  actionConfig: SuperShowcaseActionConfig | null;
  accentColor: string | null;
}

interface Draft {
  /** null while creating a new showcase. */
  id: number | null;
  title: string;
  description: string;
  isPublished: boolean;
  /** What the editor renders — a remote URL, or the data URL of a fresh pick. */
  previewUrl: string;
  /** Only set when a new file was chosen; sent to the API to replace the image. */
  imageDataUrl: string | null;
  imageWidth: number | null;
  imageHeight: number | null;
  hotspots: DraftHotspot[];
}

function rectanglePath(x: number, y: number, width: number, height: number): SuperShowcasePathPoint[] {
  const right = Math.min(100, x + width);
  const bottom = Math.min(100, y + height);
  return [{ x, y }, { x: right, y }, { x: right, y: bottom }, { x, y: bottom }];
}

type PointerMode = 'draw' | 'move' | 'resize';
type ResizeAnchor = 'n' | 's' | 'e' | 'w' | 'ne' | 'nw' | 'se' | 'sw';

interface PointerInteraction {
  mode: PointerMode;
  key?: string;
  /** Which edge/corner is being dragged when mode is `resize`. */
  anchor?: ResizeAnchor;
  startX: number;
  startY: number;
  originX: number;
  originY: number;
  originWidth: number;
  originHeight: number;
  originPathData?: SuperShowcasePathValue | null;
  moved: boolean;
}

interface Rect {
  x: number;
  y: number;
  width: number;
  height: number;
}

let hotspotKeySeed = 0;
function nextHotspotKey(): string {
  hotspotKeySeed += 1;
  return `hotspot-${Date.now()}-${hotspotKeySeed}`;
}

function clampPercent(value: number): number {
  return Math.min(100, Math.max(0, Math.round(value * 1000) / 1000));
}

function pathBounds(path: SuperShowcasePathValue | null | undefined): Rect | null {
  const points = normalizePathRings(path).flat();
  if (points.length === 0) return null;
  const xs = points.map((point) => point.x);
  const ys = points.map((point) => point.y);
  const x = Math.min(...xs);
  const y = Math.min(...ys);
  return {
    x,
    y,
    width: Math.max(MIN_HOTSPOT_PERCENT, Math.max(...xs) - x),
    height: Math.max(MIN_HOTSPOT_PERCENT, Math.max(...ys) - y),
  };
}

function translatePath(path: SuperShowcasePathValue, dx: number, dy: number): SuperShowcasePathValue {
  const bounds = pathBounds(path);
  if (!bounds) return path;
  const shiftX = Math.max(-bounds.x, Math.min(100 - (bounds.x + bounds.width), dx));
  const shiftY = Math.max(-bounds.y, Math.min(100 - (bounds.y + bounds.height), dy));
  const rings = normalizePathRings(path).map((ring) => ring.map((point) => ({ x: clampPercent(point.x + shiftX), y: clampPercent(point.y + shiftY) })));
  return Array.isArray(path) ? rings[0] : { rings };
}

function scalePath(path: SuperShowcasePathValue, origin: Rect, resized: Rect): SuperShowcasePathValue {
  const scaleX = resized.width / Math.max(origin.width, 0.0001);
  const scaleY = resized.height / Math.max(origin.height, 0.0001);
  const rings = normalizePathRings(path).map((ring) => ring.map((point) => ({
    x: clampPercent(resized.x + (point.x - origin.x) * scaleX),
    y: clampPercent(resized.y + (point.y - origin.y) * scaleY),
  })));
  return Array.isArray(path) ? rings[0] : { rings };
}

/** Resize a hotspot from a given edge/corner while clamping to the image box. */
function resizeFromAnchor(
  originX: number,
  originY: number,
  originWidth: number,
  originHeight: number,
  dx: number,
  dy: number,
  anchor: ResizeAnchor,
): Rect {
  let left = originX;
  let top = originY;
  let right = originX + originWidth;
  let bottom = originY + originHeight;

  if (anchor === 'e' || anchor === 'ne' || anchor === 'se') {
    right = clampPercent(Math.max(left + MIN_HOTSPOT_PERCENT, Math.min(100, originX + originWidth + dx)));
  }
  if (anchor === 'w' || anchor === 'nw' || anchor === 'sw') {
    left = clampPercent(Math.min(right - MIN_HOTSPOT_PERCENT, Math.max(0, originX + dx)));
  }
  if (anchor === 's' || anchor === 'se' || anchor === 'sw') {
    bottom = clampPercent(Math.max(top + MIN_HOTSPOT_PERCENT, Math.min(100, originY + originHeight + dy)));
  }
  if (anchor === 'n' || anchor === 'ne' || anchor === 'nw') {
    top = clampPercent(Math.min(bottom - MIN_HOTSPOT_PERCENT, Math.max(0, originY + dy)));
  }

  return {
    x: left,
    y: top,
    width: clampPercent(right - left),
    height: clampPercent(bottom - top),
  };
}

/** Invisible edge strips — hover/drag the border itself to resize (no mid-edge grips). */
const EDGE_HIT_ZONES: Array<{ anchor: ResizeAnchor; cursor: string; sx: Record<string, string | number> }> = [
  { anchor: 'n', cursor: 'ns-resize', sx: { top: -5, left: 10, right: 10, height: 12 } },
  { anchor: 's', cursor: 'ns-resize', sx: { bottom: -5, left: 10, right: 10, height: 12 } },
  { anchor: 'e', cursor: 'ew-resize', sx: { right: -5, top: 10, bottom: 10, width: 12 } },
  { anchor: 'w', cursor: 'ew-resize', sx: { left: -5, top: 10, bottom: 10, width: 12 } },
];

/** Visible diagonal corner grips only. */
const CORNER_HANDLES: Array<{ anchor: ResizeAnchor; cursor: string; sx: Record<string, string | number> }> = [
  { anchor: 'ne', cursor: 'nesw-resize', sx: { top: -6, right: -6 } },
  { anchor: 'nw', cursor: 'nwse-resize', sx: { top: -6, left: -6 } },
  { anchor: 'se', cursor: 'nwse-resize', sx: { bottom: -6, right: -6 } },
  { anchor: 'sw', cursor: 'nesw-resize', sx: { bottom: -6, left: -6 } },
];

function readFileAsDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(typeof reader.result === 'string' ? reader.result : '');
    reader.onerror = () => reject(new Error('Failed to read image file'));
    reader.readAsDataURL(file);
  });
}

function readImageNaturalSize(dataUrl: string): Promise<{ width: number; height: number } | null> {
  return new Promise((resolve) => {
    const image = new Image();
    image.onload = () => resolve({ width: image.naturalWidth, height: image.naturalHeight });
    image.onerror = () => resolve(null);
    image.src = dataUrl;
  });
}

function toDraft(showcase: SuperShowcase): Draft {
  return {
    id: showcase.id,
    title: showcase.title,
    description: showcase.description ?? '',
    isPublished: showcase.isPublished,
    previewUrl: showcase.imageUrl,
    imageDataUrl: null,
    imageWidth: showcase.imageWidth,
    imageHeight: showcase.imageHeight,
    hotspots: showcase.hotspots.map((hotspot) => {
      const derived = hotspot.shape === 'freehand' ? pathBounds(hotspot.pathData) : null;
      const x = hotspot.x ?? derived?.x ?? 0;
      const y = hotspot.y ?? derived?.y ?? 0;
      const width = hotspot.width ?? derived?.width ?? 0;
      const height = hotspot.height ?? derived?.height ?? 0;
      return {
      key: nextHotspotKey(),
      label: hotspot.label,
      shape: hotspot.shape,
      x,
      y,
      width,
      height,
      pathData: hotspot.pathData ?? (hotspot.shape === 'rect'
        ? rectanglePath(x, y, width, height)
        : null),
      actionType: hotspot.actionType,
      actionConfig: hotspot.actionConfig,
      accentColor: hotspot.accentColor,
    }; }),
  };
}

function toHotspotInput(hotspot: DraftHotspot): SuperShowcaseHotspotInput {
  const x = hotspot.x ?? 0;
  const y = hotspot.y ?? 0;
  const width = hotspot.width ?? 0;
  const height = hotspot.height ?? 0;
  const pathData = hotspot.pathData ?? (hotspot.shape === 'rect'
    ? rectanglePath(x, y, width, height)
    : null);
  return {
    label: hotspot.label,
    shape: hotspot.shape,
    x: hotspot.shape === 'freehand' ? null : x,
    y: hotspot.shape === 'freehand' ? null : y,
    width: hotspot.shape === 'freehand' ? null : width,
    height: hotspot.shape === 'freehand' ? null : height,
    pathData: hotspot.shape === 'freehand' ? hotspot.pathData : pathData,
    actionType: hotspot.actionType,
    // Price Promo and Thumbsup need no payload; the other two carry their own.
    actionConfig:
      hotspot.actionType === 'PROJECT_RESEARCH' || hotspot.actionType === 'LINK' || hotspot.actionType === 'REGION_MARKET_DATA'
        ? hotspot.actionConfig
        : null,
    accentColor: hotspot.accentColor,
  };
}

// ── Project Research criteria form ──────────────────────────────────────────

interface CriteriaFormProps {
  filters: MasterToolSearchFilters;
  viewMode: 'unit' | 'project';
  districtOptions: DistrictOption[];
  projectCategoryOptions: ProjectCategoryOption[];
  onFiltersChange: (next: MasterToolSearchFilters) => void;
  onViewModeChange: (next: 'unit' | 'project') => void;
}

const REGION_OPTIONS = ['CCR', 'OCR', 'RCR'];
const TENURE_OPTIONS = ['99', '999-year leasehold', 'freehold'];

function tenureLabel(value: string): string {
  if (value === '99') return '99-year';
  if (value === 'freehold') return 'Freehold';
  return '999-year';
}

/**
 * Mirrors the Project Research "Search Criteria" panel so an admin pre-fills
 * exactly the fields an agent would have typed. Values are stored verbatim as
 * `MasterToolSearchFilters`, which the app hands straight to the master tool.
 */
const ProjectResearchCriteriaForm: React.FC<CriteriaFormProps> = ({
  filters,
  viewMode,
  districtOptions,
  projectCategoryOptions,
  onFiltersChange,
  onViewModeChange,
}) => {
  const setField = (key: keyof MasterToolSearchFilters, value: unknown) => {
    onFiltersChange({ ...filters, [key]: value } as MasterToolSearchFilters);
  };

  const numericField = (
    label: string,
    minKey: keyof MasterToolSearchFilters,
    maxKey: keyof MasterToolSearchFilters,
  ) => (
    <Grid item xs={12} sm={6} md={4} key={label}>
      <Typography variant="caption" sx={{ fontWeight: 600, mb: 0.5, display: 'block', color: 'var(--color-text-strong)' }}>
        {label}
      </Typography>
      <Box sx={{ display: 'flex', gap: 1 }}>
        <TextField
          size="small"
          placeholder="Min"
          value={(filters[minKey] as string) || ''}
          onChange={(e) => setField(minKey, e.target.value.replace(/[^\d.]/g, ''))}
          inputProps={{ inputMode: 'decimal' }}
          sx={{ flex: 1 }}
        />
        <TextField
          size="small"
          placeholder="Max"
          value={(filters[maxKey] as string) || ''}
          onChange={(e) => setField(maxKey, e.target.value.replace(/[^\d.]/g, ''))}
          inputProps={{ inputMode: 'decimal' }}
          sx={{ flex: 1 }}
        />
      </Box>
    </Grid>
  );

  const selectedDistricts = (filters.districts || [])
    .map((no) => districtOptions.find((option) => option.no === no) || { no, name: null, generalLocation: null })
    .filter(Boolean) as DistrictOption[];

  const selectedCategories = (filters.projectCategories || [])
    .map((id) => projectCategoryOptions.find((option) => option.id === id) || { id, name: null })
    .filter(Boolean) as ProjectCategoryOption[];

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, flexWrap: 'wrap' }}>
        <Typography variant="caption" sx={{ fontWeight: 700, color: 'var(--color-text-strong)' }}>
          RESULTS LAND ON
        </Typography>
        <ToggleButtonGroup
          exclusive
          size="small"
          value={viewMode}
          onChange={(_, value) => value && onViewModeChange(value)}
        >
          <ToggleButton value="project">Projects view</ToggleButton>
          <ToggleButton value="unit">Units view</ToggleButton>
        </ToggleButtonGroup>
      </Box>

      <Grid container spacing={2}>
        {numericField('Price ($)', 'priceMin', 'priceMax')}
        {numericField('PSF ($/sqft)', 'psfMin', 'psfMax')}
        {numericField('Size (sqft)', 'sizeMin', 'sizeMax')}
        {numericField('Bedroom', 'bedroomMin', 'bedroomMax')}
        {numericField('Bath', 'bathMin', 'bathMax')}
        {numericField('Floor Level', 'floorMin', 'floorMax')}
        {numericField('TOP Year', 'topYearMin', 'topYearMax')}

        <Grid item xs={12} sm={6} md={4}>
          <Typography variant="caption" sx={{ fontWeight: 600, mb: 0.5, display: 'block', color: 'var(--color-text-strong)' }}>
            District
          </Typography>
          <Autocomplete
            multiple
            disableCloseOnSelect
            size="small"
            options={districtOptions}
            value={selectedDistricts}
            isOptionEqualToValue={(option, value) => option.no === value.no}
            getOptionLabel={(option) => option.name ?? option.generalLocation ?? `D${option.no}`}
            onChange={(_, value) => setField('districts', value.map((option) => option.no))}
            renderInput={(params) => <TextField {...params} placeholder="Any district" />}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip {...getTagProps({ index })} key={option.no} size="small" label={`D${option.no}`} />
              ))
            }
          />
        </Grid>

        <Grid item xs={12} sm={6} md={4}>
          <Typography variant="caption" sx={{ fontWeight: 600, mb: 0.5, display: 'block', color: 'var(--color-text-strong)' }}>
            Region
          </Typography>
          <Autocomplete
            multiple
            disableCloseOnSelect
            size="small"
            options={REGION_OPTIONS}
            value={filters.regions || []}
            onChange={(_, value) => setField('regions', value)}
            renderInput={(params) => <TextField {...params} placeholder="Any region" />}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => <Chip {...getTagProps({ index })} key={option} size="small" label={option} />)
            }
          />
        </Grid>

        <Grid item xs={12} sm={6} md={4}>
          <Typography variant="caption" sx={{ fontWeight: 600, mb: 0.5, display: 'block', color: 'var(--color-text-strong)' }}>
            Tenure
          </Typography>
          <Autocomplete
            multiple
            disableCloseOnSelect
            size="small"
            options={TENURE_OPTIONS}
            value={filters.tenure || []}
            getOptionLabel={tenureLabel}
            onChange={(_, value) => setField('tenure', value)}
            renderInput={(params) => <TextField {...params} placeholder="Any tenure" />}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip {...getTagProps({ index })} key={option} size="small" label={tenureLabel(option)} />
              ))
            }
          />
        </Grid>

        <Grid item xs={12} sm={6} md={8}>
          <Typography variant="caption" sx={{ fontWeight: 600, mb: 0.5, display: 'block', color: 'var(--color-text-strong)' }}>
            Project Category
          </Typography>
          <Autocomplete
            multiple
            disableCloseOnSelect
            size="small"
            options={projectCategoryOptions}
            value={selectedCategories}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            getOptionLabel={(option) => option.name ?? option.id}
            onChange={(_, value) => setField('projectCategories', value.map((option) => option.id))}
            renderInput={(params) => <TextField {...params} placeholder="Any category" />}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip {...getTagProps({ index })} key={option.id} size="small" label={option.name ?? option.id} />
              ))
            }
          />
        </Grid>

        <Grid item xs={12} sm={6} md={4} sx={{ display: 'flex', alignItems: 'flex-end' }}>
          <FormControlLabel
            control={
              <Checkbox
                size="small"
                checked={Boolean(filters.phOnly)}
                onChange={(e) => setField('phOnly', e.target.checked)}
              />
            }
            label={<Typography variant="body2">Penthouse units only</Typography>}
          />
        </Grid>
      </Grid>

      <Typography variant="caption" sx={{ display: 'block', mt: 1.5, color: 'var(--color-text-muted)' }}>
        Leave a field blank to leave it unfiltered. Price defaults to a $1 minimum, which is how Project Research
        excludes units without a price.
      </Typography>
    </Box>
  );
};

// ── Hotspot configuration dialog ────────────────────────────────────────────

interface HotspotConfigDialogProps {
  open: boolean;
  hotspot: DraftHotspot | null;
  isNew: boolean;
  districtOptions: DistrictOption[];
  projectCategoryOptions: ProjectCategoryOption[];
  onCancel: () => void;
  onSave: (hotspot: DraftHotspot) => void;
}

const HotspotConfigDialog: React.FC<HotspotConfigDialogProps> = ({
  open,
  hotspot,
  isNew,
  districtOptions,
  projectCategoryOptions,
  onCancel,
  onSave,
}) => {
  const [draft, setDraft] = useState<DraftHotspot | null>(hotspot);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setDraft(hotspot);
    setError(null);
  }, [hotspot]);

  if (!draft) return null;

  const researchConfig: SuperShowcaseProjectResearchConfig =
    draft.actionType === 'PROJECT_RESEARCH' && draft.actionConfig
      ? (draft.actionConfig as SuperShowcaseProjectResearchConfig)
      : { viewMode: 'project', filters: { priceMin: '1' } };

  const linkUrl =
    draft.actionType === 'LINK' ? (draft.actionConfig as SuperShowcaseLinkConfig | null)?.url ?? '' : '';

  const handleSave = () => {
    if (!draft.label.trim()) {
      setError('Give the area a label — agents see it on hover.');
      return;
    }
    if (draft.actionType === 'LINK') {
      // Save the normalized URL so a typed "huttons.com/x" is stored absolute.
      const url = normalizeShowcaseLinkUrl(linkUrl);
      if (!url) {
        setError('Enter a valid web address, e.g. https://www.huttonsgroup.com.');
        return;
      }
      onSave({ ...draft, label: draft.label.trim(), actionConfig: { url } });
      return;
    }
    onSave({
      ...draft,
      label: draft.label.trim(),
      actionConfig: draft.actionType === 'PROJECT_RESEARCH' ? researchConfig : null,
    });
  };

  return (
    <Dialog open={open} onClose={onCancel} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <HighlightAltIcon fontSize="small" />
        {isNew ? 'Map this area to a function' : 'Edit clickable area'}
      </DialogTitle>
      <DialogContent dividers>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <Grid container spacing={2} sx={{ mb: 1 }}>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              size="small"
              label="Label"
              placeholder="e.g. Value Buy band"
              value={draft.label}
              onChange={(e) => setDraft({ ...draft, label: e.target.value })}
              helperText="Shown on the area when an agent hovers or taps it."
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              select
              fullWidth
              size="small"
              label="Opens"
              value={draft.actionType}
              onChange={(e) =>
                setDraft({
                  ...draft,
                  actionType: e.target.value as SuperShowcaseActionType,
                  actionConfig:
                    e.target.value === 'REGION_MARKET_DATA'
                      ? { region: 'NORTH' }
                      : draft.actionConfig,
                  accentColor:
                    draft.accentColor ?? getShowcaseActionMeta(e.target.value as SuperShowcaseActionType).color,
                })
              }
            >
              {SHOWCASE_ACTIONS.map((action) => (
                <MenuItem key={action.type} value={action.type}>
                  {action.label}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
        </Grid>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap', mb: 2 }}>
          <Typography variant="caption" sx={{ fontWeight: 700, color: 'var(--color-text-strong)' }}>
            OUTLINE
          </Typography>
          {SHOWCASE_ACCENT_PALETTE.map((color) => {
            const selected = (draft.accentColor || getShowcaseActionMeta(draft.actionType).color) === color;
            return (
              <Box
                key={color}
                onClick={() => setDraft({ ...draft, accentColor: color })}
                sx={{
                  width: 26,
                  height: 26,
                  borderRadius: '50%',
                  bgcolor: color,
                  cursor: 'pointer',
                  border: '2px solid',
                  borderColor: selected ? 'var(--color-text-strong)' : 'rgba(var(--color-text-rgb), 0.2)',
                  boxShadow: selected ? '0 0 0 3px rgba(var(--color-text-rgb), 0.15)' : 'none',
                  transition: 'transform 120ms ease',
                  '&:hover': { transform: 'scale(1.12)' },
                }}
              />
            );
          })}
          <Divider orientation="vertical" flexItem />
          {draft.shape === 'freehand' ? (
            <Typography variant="caption" sx={{ ml: 1, color: 'text.secondary' }}>
              Generated freehand region (drawing new freehand areas is disabled)
            </Typography>
          ) : (
            <ToggleButtonGroup
              exclusive
              size="small"
              value={draft.shape}
              onChange={(_, value) => {
                if (!value) return;
                const shape = value as SuperShowcaseHotspotShape;
                setDraft({
                  ...draft,
                  shape,
                  pathData: shape === 'rect'
                    ? rectanglePath(draft.x ?? 0, draft.y ?? 0, draft.width ?? 0, draft.height ?? 0)
                    : null,
                });
              }}
            >
              <ToggleButton value="rect">Rectangle</ToggleButton>
              <ToggleButton value="ellipse">Ellipse</ToggleButton>
            </ToggleButtonGroup>
          )}
        </Box>

        {draft.actionType === 'PROJECT_RESEARCH' ? (
          <>
            <Divider sx={{ mb: 2 }} />
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5 }}>
              Pre-filled search criteria
            </Typography>
            <ProjectResearchCriteriaForm
              filters={researchConfig.filters}
              viewMode={researchConfig.viewMode}
              districtOptions={districtOptions}
              projectCategoryOptions={projectCategoryOptions}
              onFiltersChange={(filters) =>
                setDraft({ ...draft, actionConfig: { ...researchConfig, filters } })
              }
              onViewModeChange={(viewMode) =>
                setDraft({ ...draft, actionConfig: { ...researchConfig, viewMode } })
              }
            />
          </>
        ) : draft.actionType === 'LINK' ? (
          <>
            <Divider sx={{ mb: 2 }} />
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5 }}>
              Link to open
            </Typography>
            <TextField
              fullWidth
              size="small"
              label="Web address"
              placeholder="https://www.huttonsgroup.com"
              value={linkUrl}
              onChange={(e) => setDraft({ ...draft, actionConfig: { url: e.target.value } })}
              inputProps={{ maxLength: MAX_LINK_URL_LENGTH, inputMode: 'url' }}
              helperText="Opens in a new tab when an agent clicks the area. http:// or https:// only."
            />
          </>
        ) : draft.actionType === 'REGION_MARKET_DATA' ? (
          <Alert severity="info" icon={false}>
            Regional Market Comparison uses the configured region hotspot data. Keep the existing region configuration when editing generated areas.
          </Alert>
        ) : (
          <Alert severity="info" icon={false}>
            {getShowcaseActionMeta(draft.actionType).description} — no extra setup needed.
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel}>Cancel</Button>
        <Button variant="contained" onClick={handleSave}>
          {isNew ? 'Add area' : 'Save area'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ── Main dialog ─────────────────────────────────────────────────────────────

interface ManageSuperShowcaseDialogProps {
  open: boolean;
  onClose: () => void;
  districtOptions: DistrictOption[];
  projectCategoryOptions: ProjectCategoryOption[];
  /** Fired after any create / update / delete so the agent gallery can refetch. */
  onShowcasesChanged?: () => void;
}

const ManageSuperShowcaseDialog: React.FC<ManageSuperShowcaseDialogProps> = ({
  open,
  onClose,
  districtOptions,
  projectCategoryOptions,
  onShowcasesChanged,
}) => {
  const [showcases, setShowcases] = useState<SuperShowcase[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [saving, setSaving] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  const [draft, setDraft] = useState<Draft | null>(null);
  const [selectedHotspotKey, setSelectedHotspotKey] = useState<string | null>(null);
  const [pendingRect, setPendingRect] = useState<Rect | null>(null);
  const [configHotspot, setConfigHotspot] = useState<{ hotspot: DraftHotspot; isNew: boolean } | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<SuperShowcase | null>(null);
  const { stats: viewStats } = useShowcaseViewStats(open);

  const canvasRef = useRef<HTMLDivElement | null>(null);
  const interactionRef = useRef<PointerInteraction | null>(null);
  /** Mirrors `pendingRect` so pointer-up can read the final rect synchronously. */
  const pendingRectRef = useRef<Rect | null>(null);
  const newFileInputRef = useRef<HTMLInputElement | null>(null);
  const replaceFileInputRef = useRef<HTMLInputElement | null>(null);

  const loadShowcases = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setShowcases(await api.getAdminSuperShowcases());
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load Super Showcase');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!open) return;
    setDraft(null);
    setSelectedHotspotKey(null);
    setPendingRect(null);
    loadShowcases();
  }, [open, loadShowcases]);

  // ── Image picking ─────────────────────────────────────────────────────

  const readPickedImage = useCallback(async (file: File) => {
    if (!file.type.startsWith('image/')) {
      setError('Only image uploads are supported.');
      return null;
    }
    if (file.size > MAX_IMAGE_BYTES) {
      setError('Image is too large. Maximum size is 6MB.');
      return null;
    }
    const dataUrl = await readFileAsDataUrl(file);
    if (!dataUrl) {
      setError('Failed to read the image file.');
      return null;
    }
    const size = await readImageNaturalSize(dataUrl);
    return { dataUrl, size };
  }, []);

  const handleNewImagePicked = useCallback(
    async (file: File | null) => {
      if (!file) return;
      const picked = await readPickedImage(file);
      if (!picked) return;
      setError(null);
      setDraft({
        id: null,
        title: file.name.replace(/\.[^.]+$/, '').slice(0, 150),
        description: '',
        isPublished: true,
        previewUrl: picked.dataUrl,
        imageDataUrl: picked.dataUrl,
        imageWidth: picked.size?.width ?? null,
        imageHeight: picked.size?.height ?? null,
        hotspots: [],
      });
      setSelectedHotspotKey(null);
    },
    [readPickedImage],
  );

  const handleReplaceImagePicked = useCallback(
    async (file: File | null) => {
      if (!file || !draft) return;
      const picked = await readPickedImage(file);
      if (!picked) return;
      setError(null);
      setDraft({
        ...draft,
        previewUrl: picked.dataUrl,
        imageDataUrl: picked.dataUrl,
        imageWidth: picked.size?.width ?? null,
        imageHeight: picked.size?.height ?? null,
      });
    },
    [draft, readPickedImage],
  );

  // ── Drawing / moving / resizing on the preview ────────────────────────

  const pointToPercent = useCallback((clientX: number, clientY: number): { x: number; y: number } | null => {
    const container = canvasRef.current;
    if (!container) return null;
    const rect = container.getBoundingClientRect();
    if (rect.width === 0 || rect.height === 0) return null;
    return {
      x: clampPercent(((clientX - rect.left) / rect.width) * 100),
      y: clampPercent(((clientY - rect.top) / rect.height) * 100),
    };
  }, []);

  const beginInteraction = useCallback(
    (event: React.PointerEvent, mode: PointerMode, hotspot?: DraftHotspot, anchor?: ResizeAnchor) => {
      const point = pointToPercent(event.clientX, event.clientY);
      if (!point) return;
      event.preventDefault();
      (event.currentTarget as HTMLElement).setPointerCapture?.(event.pointerId);
      const derivedBounds = hotspot?.shape === 'freehand' ? pathBounds(hotspot.pathData) : null;
      const originBounds = derivedBounds ?? {
        x: hotspot?.x ?? point.x,
        y: hotspot?.y ?? point.y,
        width: hotspot?.width ?? 0,
        height: hotspot?.height ?? 0,
      };
      interactionRef.current = {
        mode,
        key: hotspot?.key,
        anchor: mode === 'resize' ? anchor ?? 'se' : undefined,
        startX: point.x,
        startY: point.y,
        originX: originBounds.x,
        originY: originBounds.y,
        originWidth: originBounds.width,
        originHeight: originBounds.height,
        originPathData: hotspot?.pathData
          ? (Array.isArray(hotspot.pathData)
            ? hotspot.pathData.map((item) => ({ ...item }))
            : { rings: normalizePathRings(hotspot.pathData).map((ring) => ring.map((item) => ({ ...item }))) })
          : null,
        moved: false,
      };
      if (mode === 'draw') {
        setSelectedHotspotKey(null);
        pendingRectRef.current = null;
        setPendingRect({ x: point.x, y: point.y, width: 0, height: 0 });
      } else if (hotspot) {
        setSelectedHotspotKey(hotspot.key);
      }
    },
    [pointToPercent],
  );

  const handleCanvasPointerMove = useCallback(
    (event: React.PointerEvent) => {
      const interaction = interactionRef.current;
      if (!interaction) return;
      const point = pointToPercent(event.clientX, event.clientY);
      if (!point) return;
      const dx = point.x - interaction.startX;
      const dy = point.y - interaction.startY;
      if (Math.abs(dx) > 0.2 || Math.abs(dy) > 0.2) interaction.moved = true;

      if (interaction.mode === 'draw') {
        const rect = {
          x: Math.min(interaction.startX, point.x),
          y: Math.min(interaction.startY, point.y),
          width: Math.abs(dx),
          height: Math.abs(dy),
        };
        pendingRectRef.current = rect;
        setPendingRect(rect);
        return;
      }

      setDraft((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          hotspots: prev.hotspots.map((hotspot) => {
            if (hotspot.key !== interaction.key) return hotspot;
            if (interaction.mode === 'move') {
              if (hotspot.shape === 'freehand' && interaction.originPathData) {
                const movedPath = translatePath(interaction.originPathData, dx, dy);
                const movedBounds = pathBounds(movedPath);
                if (!movedBounds) return hotspot;
                return { ...hotspot, ...movedBounds, pathData: movedPath };
              }
              const moved = {
                ...hotspot,
                x: clampPercent(Math.min(interaction.originX + dx, 100 - (hotspot.width ?? 0))),
                y: clampPercent(Math.min(interaction.originY + dy, 100 - (hotspot.height ?? 0))),
              };
              if (hotspot.shape === 'rect') moved.pathData = rectanglePath(moved.x ?? 0, moved.y ?? 0, moved.width ?? 0, moved.height ?? 0);
              return moved;
            }
            const resized = resizeFromAnchor(
              interaction.originX,
              interaction.originY,
              interaction.originWidth,
              interaction.originHeight,
              dx,
              dy,
              interaction.anchor ?? 'se',
            );
            if (hotspot.shape === 'freehand' && interaction.originPathData) {
              const resizedPath = scalePath(interaction.originPathData, {
                x: interaction.originX,
                y: interaction.originY,
                width: interaction.originWidth,
                height: interaction.originHeight,
              }, resized);
              const resizedBounds = pathBounds(resizedPath);
              return resizedBounds ? { ...hotspot, ...resizedBounds, pathData: resizedPath } : hotspot;
            }
            return hotspot.shape === 'rect'
              ? { ...hotspot, ...resized, pathData: rectanglePath(resized.x, resized.y, resized.width, resized.height) }
              : { ...hotspot, ...resized };
          }),
        };
      });
    },
    [pointToPercent],
  );

  const handleCanvasPointerUp = useCallback(() => {
    const interaction = interactionRef.current;
    const rect = pendingRectRef.current;
    interactionRef.current = null;
    pendingRectRef.current = null;
    setPendingRect(null);
    if (!interaction || interaction.mode !== 'draw') return;
    if (!rect || rect.width < MIN_HOTSPOT_PERCENT || rect.height < MIN_HOTSPOT_PERCENT) return;

    setConfigHotspot({
      isNew: true,
      hotspot: {
        key: nextHotspotKey(),
        label: '',
        shape: 'rect',
        x: rect.x,
        y: rect.y,
        width: rect.width,
        height: rect.height,
        pathData: rectanglePath(rect.x, rect.y, rect.width, rect.height),
        actionType: 'PROJECT_RESEARCH',
        actionConfig: { viewMode: 'project', filters: { priceMin: '1' } },
        accentColor: getShowcaseActionMeta('PROJECT_RESEARCH').color,
      },
    });
  }, []);

  const handleHotspotSaved = useCallback((hotspot: DraftHotspot) => {
    setDraft((prev) => {
      if (!prev) return prev;
      const exists = prev.hotspots.some((item) => item.key === hotspot.key);
      return {
        ...prev,
        hotspots: exists
          ? prev.hotspots.map((item) => (item.key === hotspot.key ? hotspot : item))
          : [...prev.hotspots, hotspot],
      };
    });
    setSelectedHotspotKey(hotspot.key);
    setConfigHotspot(null);
  }, []);

  const handleHotspotDelete = useCallback((key: string) => {
    setDraft((prev) => (prev ? { ...prev, hotspots: prev.hotspots.filter((item) => item.key !== key) } : prev));
    setSelectedHotspotKey((current) => (current === key ? null : current));
  }, []);

  // ── Persistence ───────────────────────────────────────────────────────

  const handleSaveDraft = useCallback(async () => {
    if (!draft) return;
    if (!draft.title.trim()) {
      setError('Title is required.');
      return;
    }
    if (!draft.id && !draft.imageDataUrl) {
      setError('Upload an image first.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const payload = {
        title: draft.title.trim(),
        description: draft.description.trim(),
        ...(draft.imageDataUrl ? { imageDataUrl: draft.imageDataUrl } : {}),
        imageWidth: draft.imageWidth,
        imageHeight: draft.imageHeight,
        isPublished: draft.isPublished,
        hotspots: draft.hotspots.map(toHotspotInput),
      };
      if (draft.id) {
        await api.updateSuperShowcase(draft.id, payload);
      } else {
        await api.createSuperShowcase(payload);
      }
      setToast(draft.id ? 'Showcase updated' : 'Showcase created');
      setDraft(null);
      await loadShowcases();
      onShowcasesChanged?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save showcase');
    } finally {
      setSaving(false);
    }
  }, [draft, loadShowcases, onShowcasesChanged]);

  const handleTogglePublished = useCallback(
    async (showcase: SuperShowcase) => {
      try {
        await api.updateSuperShowcase(showcase.id, { title: showcase.title, isPublished: !showcase.isPublished });
        setToast(showcase.isPublished ? 'Showcase hidden from agents' : 'Showcase published');
        await loadShowcases();
        onShowcasesChanged?.();
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to update showcase');
      }
    },
    [loadShowcases, onShowcasesChanged],
  );

  const handleReorder = useCallback(
    async (index: number, direction: -1 | 1) => {
      const target = showcases[index];
      const swapWith = showcases[index + direction];
      if (!target || !swapWith) return;
      try {
        await api.updateSuperShowcase(target.id, { title: target.title, sortOrder: swapWith.sortOrder });
        await api.updateSuperShowcase(swapWith.id, { title: swapWith.title, sortOrder: target.sortOrder });
        await loadShowcases();
        onShowcasesChanged?.();
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to reorder showcases');
      }
    },
    [showcases, loadShowcases, onShowcasesChanged],
  );

  const handleDeleteConfirmed = useCallback(async () => {
    if (!deleteTarget) return;
    try {
      await api.deleteSuperShowcase(deleteTarget.id);
      setToast('Showcase deleted');
      setDeleteTarget(null);
      await loadShowcases();
      onShowcasesChanged?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete showcase');
    }
  }, [deleteTarget, loadShowcases, onShowcasesChanged]);

  const selectedHotspot = useMemo(
    () => draft?.hotspots.find((hotspot) => hotspot.key === selectedHotspotKey) ?? null,
    [draft, selectedHotspotKey],
  );

  // ── Render ────────────────────────────────────────────────────────────

  const renderList = () => (
    <>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, flexWrap: 'wrap' }}>
        <Button
          variant="contained"
          startIcon={<AddPhotoAlternateIcon />}
          onClick={() => newFileInputRef.current?.click()}
          sx={{ bgcolor: 'var(--color-accent)', '&:hover': { bgcolor: 'var(--color-accent-strong)' } }}
        >
          New showcase
        </Button>
        <Typography variant="body2" sx={{ color: 'var(--color-text-muted)' }}>
          {showcases.length} showcase{showcases.length === 1 ? '' : 's'}
        </Typography>
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : showcases.length === 0 ? (
        <Alert severity="info">
          No showcases yet. Upload an image, mark out the areas agents should be able to click, and map each area to a
          function.
        </Alert>
      ) : (
        <Grid container spacing={2}>
          {showcases.map((showcase, index) => (
            <Grid item xs={12} md={6} key={showcase.id}>
              <Card sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                <Box
                  sx={{
                    position: 'relative',
                    bgcolor: 'rgba(var(--color-text-rgb), 0.05)',
                    borderBottom: '1px solid rgba(var(--color-text-rgb), 0.08)',
                  }}
                >
                  <Box
                    component="img"
                    src={showcase.imageUrl}
                    alt={showcase.title}
                    sx={{ width: '100%', height: 'auto', display: 'block' }}
                  />
                  {showcase.hotspots.map((hotspot) => {
                    const freehand = hotspot.shape === 'freehand' && normalizePathRings(hotspot.pathData).length > 0;
                    const bounds = freehand ? pathBounds(hotspot.pathData) : null;
                    return (
                    <Box
                      key={hotspot.id}
                      sx={{
                        position: 'absolute',
                        left: `${hotspot.x ?? bounds?.x ?? 0}%`,
                        top: `${hotspot.y ?? bounds?.y ?? 0}%`,
                        width: `${hotspot.width ?? bounds?.width ?? 0}%`,
                        height: `${hotspot.height ?? bounds?.height ?? 0}%`,
                        border: freehand ? 'none' : `2px solid ${getHotspotColor(hotspot)}`,
                        borderRadius: freehand ? 0 : hotspot.shape === 'ellipse' ? '50%' : '4px',
                        bgcolor: freehand ? 'transparent' : 'transparent',
                        pointerEvents: 'none',
                      }}
                    >
                      {freehand && <FreehandPolygonOverlay pathData={hotspot.pathData!} color={getHotspotColor(hotspot)} />}
                    </Box>
                    );
                  })}
                </Box>
                <CardContent sx={{ flex: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, flex: 1 }}>
                      {showcase.title}
                    </Typography>
                    <Chip
                      size="small"
                      label={showcase.isPublished ? 'Published' : 'Draft'}
                      color={showcase.isPublished ? 'success' : 'default'}
                      variant={showcase.isPublished ? 'filled' : 'outlined'}
                    />
                  </Box>
                  {showcase.description && (
                    <Typography variant="body2" sx={{ color: 'var(--color-text-muted)', mt: 0.5 }}>
                      {showcase.description}
                    </Typography>
                  )}
                  {viewStats && <ShowcaseViewStatsLine stat={viewStats.get(showcase.id)} />}
                  <Stack direction="row" spacing={0.5} sx={{ mt: 1, flexWrap: 'wrap', gap: 0.5 }}>
                    {showcase.hotspots.length === 0 ? (
                      <Chip size="small" variant="outlined" label="No clickable areas yet" />
                    ) : (
                      showcase.hotspots.map((hotspot) => (
                        <Tooltip key={hotspot.id} title={describeHotspotAction(hotspot)}>
                          <Chip
                            size="small"
                            label={hotspot.label}
                            sx={{
                              bgcolor: `${getHotspotColor(hotspot)}22`,
                              border: `1px solid ${getHotspotColor(hotspot)}`,
                            }}
                          />
                        </Tooltip>
                      ))
                    )}
                  </Stack>
                </CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, px: 1.5, pb: 1.5 }}>
                  <Button size="small" startIcon={<EditIcon />} onClick={() => { setDraft(toDraft(showcase)); setSelectedHotspotKey(null); }}>
                    Edit
                  </Button>
                  <Tooltip title={showcase.isPublished ? 'Hide from agents' : 'Publish to agents'}>
                    <IconButton size="small" onClick={() => handleTogglePublished(showcase)}>
                      {showcase.isPublished ? <VisibilityIcon fontSize="small" /> : <VisibilityOffIcon fontSize="small" />}
                    </IconButton>
                  </Tooltip>
                  <Box sx={{ flex: 1 }} />
                  <Tooltip title="Move up">
                    <span>
                      <IconButton size="small" disabled={index === 0} onClick={() => handleReorder(index, -1)}>
                        <ArrowUpwardIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip title="Move down">
                    <span>
                      <IconButton
                        size="small"
                        disabled={index === showcases.length - 1}
                        onClick={() => handleReorder(index, 1)}
                      >
                        <ArrowDownwardIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip title="Delete showcase">
                    <IconButton size="small" color="error" onClick={() => setDeleteTarget(showcase)}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}
    </>
  );

  const renderEditor = () => {
    if (!draft) return null;
    return (
      <>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, flexWrap: 'wrap' }}>
          <Button startIcon={<ArrowBackIcon />} onClick={() => setDraft(null)}>
            All showcases
          </Button>
          <Box sx={{ flex: 1 }} />
          <Button startIcon={<SwapHorizIcon />} onClick={() => replaceFileInputRef.current?.click()}>
            Replace image
          </Button>
          <Button
            variant="contained"
            startIcon={saving ? <CircularProgress size={16} sx={{ color: 'inherit' }} /> : <SaveIcon />}
            disabled={saving}
            onClick={handleSaveDraft}
            sx={{ bgcolor: 'var(--color-accent)', '&:hover': { bgcolor: 'var(--color-accent-strong)' } }}
          >
            {draft.id ? 'Save changes' : 'Create showcase'}
          </Button>
        </Box>

        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={12} md={5}>
            <TextField
              fullWidth
              size="small"
              label="Title"
              value={draft.title}
              onChange={(e) => setDraft({ ...draft, title: e.target.value })}
            />
          </Grid>
          <Grid item xs={12} md={5}>
            <TextField
              fullWidth
              size="small"
              label="Description (optional)"
              value={draft.description}
              onChange={(e) => setDraft({ ...draft, description: e.target.value })}
            />
          </Grid>
          <Grid item xs={12} md={2} sx={{ display: 'flex', alignItems: 'center' }}>
            <FormControlLabel
              control={
                <Switch
                  checked={draft.isPublished}
                  onChange={(e) => setDraft({ ...draft, isPublished: e.target.checked })}
                />
              }
              label={<Typography variant="body2">Published</Typography>}
            />
          </Grid>
        </Grid>

        <Alert severity="info" icon={<HighlightAltIcon fontSize="small" />} sx={{ mb: 2 }}>
          Drag across the image to create a rectangle, then choose its function or change it to an ellipse. Select an existing
          area to move, resize, edit, or remove it. Generated freehand regions cannot be created manually.
        </Alert>

        <Box
          ref={canvasRef}
          onPointerDown={(event) => {
            if (event.button !== 0) return;
            beginInteraction(event, 'draw');
          }}
          onPointerMove={handleCanvasPointerMove}
          onPointerUp={handleCanvasPointerUp}
          onPointerCancel={handleCanvasPointerUp}
          sx={{
            position: 'relative',
            width: '100%',
            userSelect: 'none',
            touchAction: 'none',
            cursor: 'crosshair',
            bgcolor: 'rgba(var(--color-text-rgb), 0.04)',
            borderRadius: 1,
            // Visible so edge/corner resize grips aren't clipped.
            overflow: 'visible',
          }}
        >
          <Box
            sx={{
              borderRadius: 1,
              overflow: 'hidden',
              pointerEvents: 'none',
            }}
          >
            <Box
              component="img"
              src={draft.previewUrl}
              alt={draft.title || 'Showcase'}
              draggable={false}
              sx={{ width: '100%', display: 'block' }}
            />
          </Box>

          {draft.hotspots.map((hotspot) => {
            const color = getHotspotColor(hotspot);
            const selected = hotspot.key === selectedHotspotKey;
            const freehand = hotspot.shape === 'freehand' && normalizePathRings(hotspot.pathData).length > 0;
            return (
              <Box
                key={hotspot.key}
                onPointerDown={(event) => {
                  event.stopPropagation();
                  if (event.button !== 0) return;
                  beginInteraction(event, 'move', hotspot);
                }}
                onDoubleClick={(event) => {
                  event.stopPropagation();
                  setConfigHotspot({ hotspot, isNew: false });
                }}
                sx={{
                  position: 'absolute',
                  left: `${hotspot.x}%`,
                  top: `${hotspot.y}%`,
                  width: `${hotspot.width}%`,
                  height: `${hotspot.height}%`,
                  border: freehand ? 'none' : `${selected ? 3 : 2}px solid ${color}`,
                  borderRadius: freehand ? 0 : hotspot.shape === 'ellipse' ? '50%' : '6px',
                  bgcolor: freehand ? 'transparent' : `${color}22`,
                  cursor: 'move',
                  pointerEvents: freehand ? 'none' : 'auto',
                  boxShadow: freehand ? 'none' : selected ? `0 0 0 3px ${color}44` : 'none',
                  '&:hover': { bgcolor: freehand ? 'transparent' : `${color}33` },
                  '&:hover .freehand-overlay': { opacity: 0.95 },
                }}
              >
                {freehand && (
                  <Box
                    className="freehand-overlay"
                    sx={{
                      position: 'absolute',
                      inset: 0,
                      opacity: selected ? 0.95 : 0.8,
                      clipPath: 'none',
                      bgcolor: 'transparent',
                      pointerEvents: 'auto',
                      transition: 'opacity 120ms ease, filter 120ms ease',
                    }}
                  >
                    <FreehandPolygonOverlay
                      pathData={hotspot.pathData!}
                      color={color}
                      selected={selected}
                      interactive
                      onPointerDown={(event) => {
                        event.stopPropagation();
                        if (event.button !== 0) return;
                        beginInteraction(event, 'move', hotspot);
                      }}
                      onClick={(event) => {
                        event.stopPropagation();
                        setSelectedHotspotKey(hotspot.key);
                      }}
                      onDoubleClick={(event) => {
                        event.stopPropagation();
                        setConfigHotspot({ hotspot, isNew: false });
                      }}
                    />
                  </Box>
                )}
                <Chip
                  size="small"
                  label={hotspot.label || 'Untitled'}
                  sx={{
                    position: 'absolute',
                    top: -12,
                    left: 4,
                    height: 22,
                    bgcolor: color,
                    color: '#fff',
                    fontWeight: 700,
                    fontSize: '0.68rem',
                    maxWidth: '95%',
                  }}
                />
                {EDGE_HIT_ZONES.map(({ anchor, cursor, sx: zoneSx }) => (
                  <Box
                    key={anchor}
                    onPointerDown={(event) => {
                      event.stopPropagation();
                      if (event.button !== 0) return;
                      beginInteraction(event, 'resize', hotspot, anchor);
                    }}
                    sx={{
                      position: 'absolute',
                      // Hit target only — no visible mid-edge grips.
                      bgcolor: 'transparent',
                      cursor,
                      zIndex: 1,
                      ...zoneSx,
                    }}
                  />
                ))}
                {selected &&
                  CORNER_HANDLES.map(({ anchor, cursor, sx: handleSx }) => (
                    <Box
                      key={anchor}
                      onPointerDown={(event) => {
                        event.stopPropagation();
                        if (event.button !== 0) return;
                        beginInteraction(event, 'resize', hotspot, anchor);
                      }}
                      sx={{
                        position: 'absolute',
                        width: 12,
                        height: 12,
                        borderRadius: '3px',
                        bgcolor: color,
                        border: '2px solid #fff',
                        boxShadow: '0 1px 3px rgba(0,0,0,0.35)',
                        cursor,
                        zIndex: 2,
                        ...handleSx,
                      }}
                    />
                  ))}
              </Box>
            );
          })}

          {pendingRect && pendingRect.width > 0 && (
            <Box
              sx={{
                position: 'absolute',
                left: `${pendingRect.x}%`,
                top: `${pendingRect.y}%`,
                width: `${pendingRect.width}%`,
                height: `${pendingRect.height}%`,
                border: '2px dashed var(--color-accent)',
                bgcolor: 'rgba(var(--color-accent-rgb), 0.15)',
                pointerEvents: 'none',
              }}
            />
          )}

        </Box>

        <Box sx={{ mt: 2 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
            Clickable areas ({draft.hotspots.length})
          </Typography>
          {draft.hotspots.length === 0 ? (
            <Typography variant="body2" sx={{ color: 'var(--color-text-muted)' }}>
              No clickable areas configured.
            </Typography>
          ) : (
            <Stack spacing={1}>
              {draft.hotspots.map((hotspot) => {
                const color = getHotspotColor(hotspot);
                return (
                  <Box
                    key={hotspot.key}
                    onClick={() => setSelectedHotspotKey(hotspot.key)}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1.5,
                      p: 1,
                      borderRadius: 1,
                      cursor: 'pointer',
                      border: '1px solid',
                      borderColor:
                        hotspot.key === selectedHotspotKey ? color : 'rgba(var(--color-text-rgb), 0.12)',
                      bgcolor: hotspot.key === selectedHotspotKey ? `${color}14` : 'transparent',
                    }}
                  >
                    <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: color, flexShrink: 0 }} />
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {hotspot.label || 'Untitled'}
                        <Typography component="span" variant="caption" sx={{ ml: 1, color: 'var(--color-text-muted)' }}>
                          {getShowcaseActionMeta(hotspot.actionType).label}
                        </Typography>
                      </Typography>
                      <Typography variant="caption" sx={{ color: 'var(--color-text-muted)' }}>
                        {describeHotspotAction(hotspot)}
                      </Typography>
                    </Box>
                    <IconButton size="small" onClick={(e) => { e.stopPropagation(); setConfigHotspot({ hotspot, isNew: false }); }}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      color="error"
                      onClick={(e) => { e.stopPropagation(); handleHotspotDelete(hotspot.key); }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>
                );
              })}
            </Stack>
          )}
          {selectedHotspot && (
            <Typography variant="caption" sx={{ display: 'block', mt: 1, color: 'var(--color-text-muted)' }}>
              Selected: {selectedHotspot.label || 'Untitled'} — double-click the area on the image to edit it.
            </Typography>
          )}
        </Box>
      </>
    );
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} fullScreen>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <HighlightAltIcon />
          <Box sx={{ flex: 1 }}>
            Manage Super Showcase
            <Typography variant="caption" sx={{ display: 'block', color: 'var(--color-text-muted)', fontWeight: 400 }}>
              Interactive images agents can click into
            </Typography>
          </Box>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}
          {draft ? renderEditor() : renderList()}
        </DialogContent>

        <input
          ref={newFileInputRef}
          type="file"
          accept="image/png,image/jpeg,image/webp"
          hidden
          onChange={(e) => {
            handleNewImagePicked(e.target.files?.[0] ?? null);
            e.target.value = '';
          }}
        />
        <input
          ref={replaceFileInputRef}
          type="file"
          accept="image/png,image/jpeg,image/webp"
          hidden
          onChange={(e) => {
            handleReplaceImagePicked(e.target.files?.[0] ?? null);
            e.target.value = '';
          }}
        />
      </Dialog>

      <HotspotConfigDialog
        open={Boolean(configHotspot)}
        hotspot={configHotspot?.hotspot ?? null}
        isNew={configHotspot?.isNew ?? false}
        districtOptions={districtOptions}
        projectCategoryOptions={projectCategoryOptions}
        onCancel={() => setConfigHotspot(null)}
        onSave={handleHotspotSaved}
      />

      <Dialog open={Boolean(deleteTarget)} onClose={() => setDeleteTarget(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Delete showcase?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            "{deleteTarget?.title}" and its clickable areas will stop appearing for agents. This cannot be undone from
            the app.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={handleDeleteConfirmed}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={Boolean(toast)}
        autoHideDuration={3000}
        onClose={() => setToast(null)}
        message={toast ?? ''}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </>
  );
};

export default ManageSuperShowcaseDialog;
